import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useChecklistGeral } from '@hooks/useChecklistGeral.js'
import { useProximoPasso } from '@hooks/useProximoPasso.js'
import { getModulosJornada } from '@content/jornada.js'
import { topicoDetailPath } from '@utils/constants.js'
import { PageShell } from '@components/layout/PageShell.js'
import { JornadaPageSkeleton } from '@components/primitives/Skeleton.js'
import { ProgressRing } from '@components/primitives/ProgressRing.js'
import { JornadaSearch } from '@components/guia/JornadaSearch.js'
import { JornadaProgresso } from '@components/guia/JornadaProgresso.js'
import { ChecklistPendentesView } from '@components/guia/ChecklistPendentesView.js'
import { useChecklistPendentes } from '@hooks/useChecklistPendentes.js'
import { computeAlertaSequencia } from '@utils/alertaSequencia.js'
import type { TopicoStatus } from '@hooks/useModuloDetail.js'

type Vista = 'passos' | 'pendentes' | 'progresso'

export function JornadaPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { modulos, isLoading } = useChecklistGeral()
  const { modulos: modulosPendentes, totalItens, toggleItem } = useChecklistPendentes()
  const proximo = useProximoPasso()

  const vistaInicial = params.get('vista') === 'progresso' ? 'progresso' : 'passos'
  const [vista, setVista] = useState<Vista>(vistaInicial)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['terreno']))

  useEffect(() => {
    const mod = params.get('modulo')
    if (mod) setExpanded((prev) => new Set(prev).add(mod))
    if (params.get('vista') === 'progresso') setVista('progresso')
  }, [params])

  const jornada = useMemo(() => getModulosJornada(), [])
  const totalConcluidos = modulos.reduce((s, m) => s + m.concluidos, 0)
  const totalPassos = modulos.reduce((s, m) => s + m.total, 0)
  const pct = totalPassos > 0 ? Math.round((totalConcluidos / totalPassos) * 100) : 0
  const moduloEmFoco = useMemo(() => {
    const emAndamento = modulos.find((m) => m.status === 'em-andamento')
    if (emAndamento) return emAndamento
    return modulos.find((m) => m.pct < 100) ?? modulos[modulos.length - 1]
  }, [modulos])
  const alertaPert = computeAlertaSequencia(modulos)

  const statusMap = useMemo(() => {
    const map = new Map<string, TopicoStatus>()
    for (const m of modulos) {
      for (const t of m.topicos) map.set(t.id, t.status)
    }
    return map
  }, [modulos])

  const passoAtivoId = useMemo(() => {
    for (const m of modulos) {
      for (const t of m.topicos) {
        if (t.status === 'em-andamento') return t.id
      }
    }
    for (const m of modulos) {
      for (const t of m.topicos) {
        if (t.status === 'nao-iniciado') return t.id
      }
    }
    return null
  }, [modulos])

  const setVistaAndUrl = useCallback(
    (v: Vista) => {
      setVista(v)
      if (v === 'progresso') setParams({ vista: 'progresso' })
      else setParams({})
    },
    [setParams],
  )

  const toggleModulo = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  if (isLoading) {
    return (
      <PageShell variant='guia-hero'>
        <JornadaPageSkeleton label='Carregando jornada…' />
      </PageShell>
    )
  }

  return (
    <PageShell variant='guia-hero'>
      <header className='cf3-jornada-hero cf3-jornada-hero--v2'>
        <div className='cf3-jornada-hero__head'>
          <div className='cf3-jornada-hero__headline'>
            <h1 className='cf3-jornada-hero__title'>Sua jornada</h1>
            <ProgressRing
              value={pct}
              size={55}
              stroke={7}
              label=''
              onDark
              showPct
              textScale={1.1}
              ariaLabel={`${pct}% da jornada`}
            />
          </div>
          <p className='cf3-jornada-hero__meta'>
            {moduloEmFoco
              ? `${moduloEmFoco.titulo}: ${moduloEmFoco.pct}% concluído neste módulo`
              : 'Todos os módulos concluídos'}
          </p>
          {proximo && (
            <p className='cf3-jornada-hero__next'>
              <span className='cf3-jornada-hero__next-label'>Próxima decisão:</span>{' '}
              {proximo.topicoTitulo}
            </p>
          )}
        </div>
        {proximo && (
          <button
            type='button'
            className='cf3-jornada-hero__cta'
            aria-label={`Ir para próxima decisão: ${proximo.topicoTitulo}`}
            onClick={() => navigate(topicoDetailPath(proximo.moduloId, proximo.topicoId))}
          >
            Ir para esta etapa
            <span className='cf3-jornada-hero__cta-arrow' aria-hidden>
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M3 8h10M9 4l4 4-4 4' />
              </svg>
            </span>
          </button>
        )}
      </header>

      {alertaPert && vista !== 'progresso' && (
        <div className='cf3-progresso__alerta' role='alert'>
          <p className='cf3-progresso__alerta-title'>Atenção à ordem</p>
          <p className='cf3-progresso__alerta-text'>{alertaPert}</p>
        </div>
      )}

      <JornadaSearch />

      <div
        className='cf3-segment cf3-segment--full'
        role='tablist'
        aria-label='Visualização da jornada'
      >
        {(
          [
            ['passos', GUIA_LABELS.todasEtapas],
            ['pendentes', totalItens > 0 ? `Pendentes (${totalItens})` : 'Pendentes'],
            ['progresso', 'Progresso'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type='button'
            role='tab'
            aria-selected={vista === id}
            className={['cf3-segment__btn', vista === id ? 'cf3-segment__btn--active' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setVistaAndUrl(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {vista === 'progresso' ? (
        <JornadaProgresso modulos={modulos} />
      ) : vista === 'pendentes' ? (
        <ChecklistPendentesView
          modulos={modulosPendentes}
          totalItens={totalItens}
          onToggleItem={toggleItem}
          compact
        />
      ) : (
        <div className='cf3-jornada-timeline'>
          {jornada.map((mod, modIdx) => {
            const modProgress = modulos.find((m) => m.id === mod.id)
            const isOpen = expanded.has(mod.id)
            const modPct = modProgress?.pct ?? 0
            const isModDone = modPct >= 100
            const isModActive = modProgress?.status === 'em-andamento'
            const isLastMod = modIdx === jornada.length - 1

            const passosVisiveis = mod.passos

            return (
              <section
                key={mod.id}
                className={[
                  'cf3-modulo',
                  isOpen ? 'cf3-modulo--open' : '',
                  isModDone ? 'cf3-modulo--done' : '',
                  isModActive ? 'cf3-modulo--active' : '',
                  isLastMod ? 'cf3-modulo--last' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className='cf3-modulo__spine' aria-hidden>
                  <span className='cf3-modulo__marker'>{isModDone ? '✓' : `${modPct}%`}</span>
                </div>
                {!isLastMod && <span className='cf3-modulo__rail' aria-hidden />}

                <button
                  type='button'
                  className='cf3-modulo__head'
                  onClick={() => toggleModulo(mod.id)}
                  aria-expanded={isOpen}
                  aria-label={`${mod.titulo}, ${modPct}% concluído`}
                >
                  <span className='cf3-modulo__head-body'>
                    <span className='cf3-modulo__title-row'>
                      <span className='cf3-modulo__title'>{mod.titulo}</span>
                      <svg
                        className='cf3-modulo__chevron'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        aria-hidden
                      >
                        <path
                          d='M4 6l4 4 4-4'
                          stroke='currentColor'
                          strokeWidth='1.75'
                          fill='none'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                    <span
                      className='cf3-modulo__progress-track'
                      role='progressbar'
                      aria-valuenow={modPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${mod.titulo}: ${modPct}% concluído`}
                    >
                      <span
                        className='cf3-modulo__progress-fill'
                        style={{ ['--cf3-bar-w' as string]: `${modPct}%` }}
                      />
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <ul className='cf3-passos'>
                    {passosVisiveis.map((passo, idx) => {
                      const st = statusMap.get(passo.topicoId) ?? 'nao-iniciado'
                      const isAtivo = passo.topicoId === passoAtivoId
                      const isLast = idx === passosVisiveis.length - 1
                      const nodeState =
                        st === 'concluido' ? 'concluido' : isAtivo ? 'atual' : 'pendente'
                      return (
                        <li
                          key={passo.topicoId}
                          className={[
                            'cf3-passos__item',
                            `cf3-passos__item--${nodeState}`,
                            isLast ? 'cf3-passos__item--last' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span className='cf3-passos__node' aria-hidden />
                          <button
                            type='button'
                            className={['cf3-passo-row', `cf3-passo-row--${nodeState}`]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() =>
                              navigate(topicoDetailPath(passo.moduloId, passo.topicoId))
                            }
                          >
                            <span className='cf3-passo-row__title'>{passo.titulo}</span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}
    </PageShell>
  )
}
